import { Box, Typography, Button } from '@mui/material'
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

const Home: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date())
  const [countdownTime, setCountdownTime] = useState(new Date())
  const navigate = useNavigate()
  const { user } = useAuth()

  const handleStudioClick = () => {
    if (user) {
      navigate('/studio')
    } else {
      navigate('/auth')
    }
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      setCountdownTime(new Date())
    }, 33)
    return () => clearInterval(interval)
  }, [])

  const formatTimeEST = (date: Date): string => {
    const now = new Date(date.getTime())
    const offsetEST = -5 * 60 // EST is UTC-5 (during standard time)
    const offsetDST = -4 * 60 // EDT is UTC-4 (during daylight time)
    
    // Simple DST check (March to November)
    const month = now.getUTCMonth()
    const isDST = month >= 2 && month <= 10
    const offset = isDST ? offsetDST : offsetEST
    
    const estTime = new Date(now.getTime() + (offset * 60 * 1000))
    
    const hours = estTime.getUTCHours().toString().padStart(2, '0')
    const minutes = estTime.getUTCMinutes().toString().padStart(2, '0')
    const seconds = estTime.getUTCSeconds().toString().padStart(2, '0')
    
    return `${hours}:${minutes}:${seconds}`
  }

  const formatMarketCountdown = (date: Date): string => {
    const now = new Date(date.getTime())
    const offsetEST = -5 * 60
    const offsetDST = -4 * 60
    
    const month = now.getUTCMonth()
    const isDST = month >= 2 && month <= 10
    const offset = isDST ? offsetDST : offsetEST
    
    const estTime = new Date(now.getTime() + (offset * 60 * 1000))
    const currentTotalMs = estTime.getUTCHours() * 60 * 60 * 1000 + 
                          estTime.getUTCMinutes() * 60 * 1000 + 
                          estTime.getUTCSeconds() * 1000 + 
                          estTime.getUTCMilliseconds()
    
    const marketOpen = 9.5 * 60 * 60 * 1000  // 9:30 AM
    const marketClose = 16 * 60 * 60 * 1000   // 4:00 PM
    
    let targetTime: number
    let label: string
    
    if (currentTotalMs < marketOpen) {
      targetTime = marketOpen
      label = "// US MARKETS OPEN >>"
    } else if (currentTotalMs < marketClose) {
      targetTime = marketClose
      label = "// US MARKETS CLOSE >>"
    } else {
      targetTime = marketOpen + (24 * 60 * 60 * 1000)
      label = "// US MARKETS OPEN >>"
    }
    
    const remainingMs = targetTime - currentTotalMs
    const remainingHours = Math.floor(remainingMs / (60 * 60 * 1000))
    const remainingMinutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000))
    const remainingSeconds = Math.floor((remainingMs % (60 * 1000)) / 1000)
    const remainingCentiseconds = Math.floor((remainingMs % 1000) / 10)
    
    const hoursStr = remainingHours.toString().padStart(2, '0')
    const minutesStr = remainingMinutes.toString().padStart(2, '0')
    const secondsStr = remainingSeconds.toString().padStart(2, '0')
    const centisecondsStr = remainingCentiseconds.toString().padStart(2, '0')
    
    return `${label} ${hoursStr}:${minutesStr}:${secondsStr}.${centisecondsStr}`
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#000000',
        position: 'relative',
        overflow: 'hidden',
        '@keyframes blink': {
          '0%': { opacity: 1 },
          '50%': { opacity: 0 },
          '100%': { opacity: 1 },
        },
      }}
    >
      {/* Header */}
      <Box
        component="header"
        sx={{
          padding: {
            xs: '5px 15px',
            sm: '5px 20px',
            md: '5px',
            lg: '25px 20px',
          },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* Logo Area */}
        <Typography
          variant="h6"
          sx={{
            color: '#C0C0C0',
            fontFamily: '"Montserrat Bold", monospace',
            fontWeight: 'bold',
            fontSize: {
              xs: '2.0rem',
              sm: '2.0rem',
              md: '2.0rem',
              lg: '2.0rem',
            },
          }}
        >
          {/* Empty header - branding is in center */}
        </Typography>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="text"
            onClick={handleStudioClick}
            sx={{
              color: '#C0C0C0',
              fontFamily: '"Montserrat SemiBold", monospace',
              fontSize: {
                xs: '0.6rem',
                sm: '0.7rem',
                md: '0.8rem',
              },
              padding: {
                xs: '4px 8px',
                sm: '6px 12px',
                md: '8px 16px',
              },
              '&:hover': {
                color: '#ffffff',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {user ? 'Model Studio' : 'Sign In'}
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          position: 'relative',
          zIndex: 10,
          width: '100%',
          minHeight: {
            xs: 'calc(100vh - 80px)',
            md: 'auto',
          },
          overflow: 'hidden',
        }}
      >
        {/* Background Video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 1,
            opacity: 0.3,
          }}
          onError={(e) => {
            console.error('Video error:', e)
            // Try to reload the video after an error
            const video = e.target as HTMLVideoElement
            setTimeout(() => {
              video.load()
              video.play().catch(console.error)
            }, 1000)
          }}
          onLoadStart={() => console.log('Video loading started')}
          onCanPlay={(e) => {
            console.log('Video can play')
            const video = e.target as HTMLVideoElement
            video.play().catch(console.error)
          }}
          onStalled={(e) => {
            console.log('Video stalled, attempting to restart')
            const video = e.target as HTMLVideoElement
            video.currentTime = 0
            video.play().catch(console.error)
          }}
          onWaiting={() => {
            console.log('Video waiting for data')
          }}
        >
          <source src="/background-video-2.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* A/TOS Terminal Branding and Countdown Timer Display */}
        <Box
          sx={{
            textAlign: 'left',
            position: 'relative',
            zIndex: 20,
          }}
        >
          {/* A/TOS Terminal Text with Blinking Cursor */}
          <Typography
            sx={{
              color: '#ff00008e',
              fontFamily: '"Lucida Console", monospace',
              fontSize: {
                xs: '2.5rem',    // Increased from 1.5rem
                sm: '3.5rem',    // Increased from 2.0rem
                md: '9.0rem',    // Increased from 2.5rem
                lg: '9.0rem',    // Increased from 3.0rem
              },
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              marginBottom: '0rem',
            }}
          >
            A/TOS&gt;
            <span style={{ 
              animation: 'blink 1s infinite',
            }}>
              _
            </span>
          </Typography>

          {/* Market Countdown Timer */}
          <Typography
            sx={{
              color: '#c0c0c09a',
              fontFamily: '"Montserrat Bold", monospace',
              fontSize: {
                xs: '0.5rem',
                sm: '0.6rem',
                md: '1.5rem',
                lg: '1.5rem',
              },
              fontWeight: 'bold',
              letterSpacing: '0.1em',
            }}
          >
            {formatMarketCountdown(countdownTime)}
          </Typography>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          padding: {
            xs: '10px 15px',
            sm: '15px 20px',
            md: '20px',
          },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {/* EST Clock in Footer */}
        <Typography
          sx={{
            color: '#C0C0C0',
            fontFamily: '"Montserrat Bold", monospace',
            fontSize: {
              xs: '0.4rem',
              sm: '0.45rem',
              md: '0.5rem',
              lg: '0.6rem',
            },
            fontWeight: 'SemiBold',
            letterSpacing: '0.02em',
            marginBottom: '0.0rem',
          }}
        >
          // EST {formatTimeEST(currentTime)}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: '#C0C0C0',
            fontFamily: '"Montserrat Bold", monospace',
            fontSize: {
              xs: '0.4rem',
              sm: '0.45rem',
              md: '0.5rem',
              lg: '0.6rem',
            },
          }}
        >
          // (c) 2025 ATOS TRADING INC
        </Typography>
      </Box>
    </Box>
  )
}

export default Home
